<script setup>
// SICARG: reporte de tiempo conectado por agente en un rango de fechas.
// No-admin: el backend fuerza el scoping a su propio user_id (ver
// ReportsController#agent_connected_time_user_id) — la UI ademas oculta el
// selector de agente para no sugerir que puede elegir a otro.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ReportsAPI from 'dashboard/api/reports';
import ReportHeader from './components/ReportHeader.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();

const currentRole = useMapGetter('getCurrentRole');
const currentUserId = useMapGetter('getCurrentUserID');
const agents = useMapGetter('agents/getAgents');

const isAdmin = computed(() => currentRole.value === 'administrator');

const toDateInputValue = date => date.toISOString().slice(0, 10);
const startOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const since = ref(toDateInputValue(startOfToday()));
const until = ref(toDateInputValue(new Date()));
const selectedAgentId = ref('');

const isLoading = ref(false);
const trackerAvailableFrom = ref(null);
const rows = ref([]);

const agentNameFor = userId => {
  const agent = agents.value.find(item => item.id === userId);
  return agent ? agent.name : `#${userId}`;
};

const formatDuration = seconds => {
  const total = Math.max(0, Math.round(seconds || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const formatTimestamp = epochSeconds => {
  if (!epochSeconds) return '—';
  return new Date(epochSeconds * 1000).toLocaleString();
};

const epochFromDateInput = (value, endOfDay = false) => {
  const date = new Date(value);
  if (endOfDay) date.setHours(23, 59, 59, 999);
  else date.setHours(0, 0, 0, 0);
  return Math.floor(date.getTime() / 1000);
};

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await ReportsAPI.getAgentConnectedTime({
      from: epochFromDateInput(since.value),
      to: epochFromDateInput(until.value, true),
      userId: isAdmin.value ? selectedAgentId.value || undefined : undefined,
    });
    trackerAvailableFrom.value = data.tracker_available_from;
    rows.value = data.agents || [];
  } catch (error) {
    useAlert(t('AGENT_CONNECTED_TIME_REPORTS.API.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const rangeStartsBeforeTracker = computed(() => {
  if (!trackerAvailableFrom.value) return true;
  return epochFromDateInput(since.value) < trackerAvailableFrom.value;
});

onMounted(() => {
  if (isAdmin.value) store.dispatch('agents/get');
  load();
});
</script>

<template>
  <ReportHeader
    :header-title="t('AGENT_CONNECTED_TIME_REPORTS.HEADER')"
    :header-description="t('AGENT_CONNECTED_TIME_REPORTS.DESCRIPTION')"
  />

  <div class="flex flex-col gap-6">
    <div class="flex flex-wrap items-end gap-3">
      <div class="flex flex-col gap-1.5">
        <label class="text-xs text-n-slate-11">
          {{ t('AGENT_CONNECTED_TIME_REPORTS.FILTER.SINCE') }}
        </label>
        <NextInput v-model="since" type="date" />
      </div>
      <div class="flex flex-col gap-1.5">
        <label class="text-xs text-n-slate-11">
          {{ t('AGENT_CONNECTED_TIME_REPORTS.FILTER.UNTIL') }}
        </label>
        <NextInput v-model="until" type="date" />
      </div>
      <div v-if="isAdmin" class="flex flex-col gap-1.5">
        <label class="text-xs text-n-slate-11">
          {{ t('AGENT_CONNECTED_TIME_REPORTS.FILTER.AGENT') }}
        </label>
        <select
          v-model="selectedAgentId"
          class="w-48 !h-10 !min-h-0 !m-0 px-3 text-sm leading-tight rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak border-none"
        >
          <option value="">
            {{ t('AGENT_CONNECTED_TIME_REPORTS.FILTER.ALL') }}
          </option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
      </div>
      <NextButton
        blue
        sm
        :is-loading="isLoading"
        :label="t('AGENT_CONNECTED_TIME_REPORTS.FILTER.APPLY')"
        @click="load"
      />
    </div>

    <p
      v-if="rangeStartsBeforeTracker"
      class="text-sm text-n-amber-11"
    >
      {{ t('AGENT_CONNECTED_TIME_REPORTS.NO_DATA_BEFORE_TRACKER') }}
    </p>

    <div class="overflow-x-auto rounded-lg border border-n-weak">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak text-left text-n-slate-11">
            <th v-if="isAdmin" class="px-4 py-2">
              {{ t('AGENT_CONNECTED_TIME_REPORTS.TABLE.AGENT') }}
            </th>
            <th class="px-4 py-2">
              {{ t('AGENT_CONNECTED_TIME_REPORTS.TABLE.CONNECTED_TIME') }}
            </th>
            <th class="px-4 py-2">
              {{ t('AGENT_CONNECTED_TIME_REPORTS.TABLE.SESSIONS') }}
            </th>
            <th class="px-4 py-2">
              {{ t('AGENT_CONNECTED_TIME_REPORTS.TABLE.FIRST_CONNECTED') }}
            </th>
            <th class="px-4 py-2">
              {{ t('AGENT_CONNECTED_TIME_REPORTS.TABLE.LAST_CONNECTED') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.user_id"
            class="border-b border-n-weak/40"
          >
            <td v-if="isAdmin" class="px-4 py-2 text-n-slate-12">
              {{ agentNameFor(row.user_id) }}
            </td>
            <td class="px-4 py-2 font-medium text-n-slate-12">
              {{ formatDuration(row.connected_seconds) }}
            </td>
            <td class="px-4 py-2 text-n-slate-11">{{ row.sessions_count }}</td>
            <td class="px-4 py-2 text-n-slate-11">
              {{ formatTimestamp(row.first_connected_at) }}
            </td>
            <td class="px-4 py-2 text-n-slate-11">
              {{ formatTimestamp(row.last_connected_at) }}
            </td>
          </tr>
          <tr v-if="!isLoading && !rows.length">
            <td
              :colspan="isAdmin ? 5 : 4"
              class="px-4 py-6 text-center text-n-slate-10"
            >
              {{ t('AGENT_CONNECTED_TIME_REPORTS.NO_RECORDS') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
